import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, Clock, ShieldCheck, ChevronDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBidWlsZGluZyUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzkxODE2MzcxfDA&ixlib=rb-4.1.0&q=85";

const TITLE_LINES = [
  { plain: "Membangun dengan ", accent: "Kualitas," },
  { plain: "Mengutamakan ", accent: "Kepercayaan" },
];

const BADGES = [
  { icon: BadgeCheck, label: "Berpengalaman", testId: "hero-badge-experience" },
  { icon: Clock, label: "Tepat Waktu", testId: "hero-badge-punctual" },
  { icon: ShieldCheck, label: "Pengerjaan Bergaransi", testId: "hero-badge-warranty" },
];

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.25 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (e, id) => {
    e.preventDefault();
    scrollToSection(`#${id}`);
  };

  return (
    <section
      id="beranda"
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy"
      aria-label="Bagian utama"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
        <img
          src={HERO_IMAGE}
          alt=""
          className="h-[120%] w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/40"
        aria-hidden="true"
      />
      <div className="navy-grid-bg absolute inset-0 opacity-60" aria-hidden="true" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-12"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-400 backdrop-blur-sm"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-orange-500" aria-hidden="true" />
          Kontraktor Konstruksi &amp; Renovasi
        </motion.p>

        <h1
          data-testid="hero-title"
          className="text-balance font-heading text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {TITLE_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
              >
                {line.plain}
                <span className="italic text-orange-500">{line.accent}</span>
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-description"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          Solusi konstruksi, renovasi, dan pembangunan yang direncanakan dengan
          matang serta dikerjakan oleh tim profesional.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#kontak"
            data-testid="hero-cta-primary"
            onClick={(e) => go(e, "kontak")}
            className="group inline-flex items-center gap-2.5 rounded-full bg-orange-500 px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-orange-500/25 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
          >
            Konsultasi Gratis
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#proyek"
            data-testid="hero-cta-secondary"
            onClick={(e) => go(e, "proyek")}
            className="inline-flex items-center gap-2.5 rounded-full border border-white/30 px-7 py-3.5 text-sm font-bold text-white transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/60 hover:bg-white/10"
          >
            Lihat Proyek Kami
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          aria-label="Ringkasan keunggulan"
        >
          {BADGES.map((badge) => (
            <li
              key={badge.testId}
              data-testid={badge.testId}
              className="flex items-center gap-2.5 text-sm font-semibold text-slate-200"
            >
              <badge.icon className="h-5 w-5 text-orange-500" aria-hidden="true" />
              {badge.label}
            </li>
          ))}
        </motion.ul>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => scrollToSection("#tentang")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-400 transition-colors hover:text-orange-400 md:flex"
        aria-label="Gulir ke bawah"
      >
        Gulir
        <motion.span
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};
